import { useEffect, useState, useRef } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Upload, Plus, Trash2, Save, Download, FileText, Loader2, Calculator, ArrowLeft, Lock, X, FolderPlus } from "lucide-react";

const HEADER_FIELDS = [
  ["bank", "Bank name"], ["account_holder", "Customer name"], ["customer_id", "Customer ID"],
  ["account_number", "Account number"], ["ifsc", "IFSC"], ["micr", "MICR"],
  ["branch", "Account branch"], ["branch_address", "Branch address"], ["address", "Communication address"],
  ["email", "Email ID"], ["phone", "Phone no"], ["ckyc_id", "CKYC ID"],
  ["account_opening_date", "Account opening date"], ["account_status", "Account status"], ["account_type", "Account type"],
  ["nomination", "Nomination"], ["nominee_name", "Nominee name"], ["currency", "Currency"],
  ["period_from", "Statement period from"], ["period_to", "Statement period to"], ["opening_balance", "Opening balance"],
];
const COLS = ["Txn Date", "Value Date", "Particulars", "Debit", "Credit", "Balance"];
const KEYS = ["txn_date", "value_date", "particulars", "debit", "credit", "balance"];
const FED_COLS = ["Date", "Value Date", "Particulars", "Tran Type", "Cheque Details", "Withdrawals", "Deposits", "Balance"];
const FED_KEYS = ["txn_date", "value_date", "particulars", "tran_type", "cheque_details", "debit", "credit", "balance"];
const FEDERAL_FIELDS = [
  ["scheme", "Scheme"], ["swift_code", "SWIFT Code"], ["branch_sol_id", "Branch Sol ID"],
  ["mode_of_operation", "Mode of Operation"], ["joint_holders", "Joint Holders"],
  ["address_last_updated", "Address Last Updated On"], ["effective_available_balance", "Effective Available Balance"],
];
const BANK_OPTIONS = ["IDFC FIRST Bank", "HDFC Bank", "State Bank of India", "ICICI Bank", "Kotak Mahindra Bank", "Axis Bank", "Bank of Baroda", "Federal Bank"];
// Realistic sample branch/IFSC/MICR per bank so a freshly-picked template looks authentic.
const BANK_SAMPLES = {
  "IDFC FIRST Bank": { ifsc: "IDFB0040101", micr: "400751002", branch: "Sanpada - Navi Mumbai", branch_address: "Shop 5-8, Grohitam CHSL, Sector 19, Sanpada, Navi Mumbai 400705", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "HDFC Bank": { ifsc: "HDFC0000123", micr: "400240004", branch: "Vashi - Navi Mumbai", branch_address: "Plot 12, Sector 17, Vashi, Navi Mumbai 400703", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "State Bank of India": { ifsc: "SBIN0000456", micr: "400002007", branch: "Nariman Point - Mumbai", branch_address: "SBI Bhavan, Madame Cama Road, Nariman Point, Mumbai 400021", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "ICICI Bank": { ifsc: "ICIC0000234", micr: "400229002", branch: "Bandra Kurla Complex - Mumbai", branch_address: "ICICI Bank Towers, BKC, Bandra East, Mumbai 400051", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "Kotak Mahindra Bank": { ifsc: "KKBK0000321", micr: "400485003", branch: "BKC - Mumbai", branch_address: "27 BKC, C-27, G Block, Bandra East, Mumbai 400051", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "Axis Bank": { ifsc: "UTIB0000199", micr: "400211005", branch: "Fort - Mumbai", branch_address: "Universal Insurance Bldg, Sir P M Road, Fort, Mumbai 400001", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "Bank of Baroda": { ifsc: "BARB0VASHIX", micr: "400012006", branch: "Vashi - Navi Mumbai", branch_address: "Sector 17, Vashi, Navi Mumbai 400703", account_type: "SAVINGS", account_status: "ACTIVE", currency: "INR" },
  "Federal Bank": { ifsc: "FDRL0002708", micr: "", swift_code: "FDRLINBBIBD", branch_sol_id: "2708", branch: "Mumbai / Vashi - APMC Market", branch_address: "APMC Market, Vashi, Navi Mumbai 400703", scheme: "SWEEP PLUS CURRENT ACCOUNT", mode_of_operation: "SINGLE", joint_holders: "NIL", account_type: "Current Account", account_status: "ACTIVE", nomination: "REGISTERED", currency: "INR" },
};
const num = (v) => (v === "" || v === null || v === undefined ? null : (isNaN(parseFloat(v)) ? v : parseFloat(v)));

export default function BankStatements() {
  const { companyId } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pwFile, setPwFile] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwError, setPwError] = useState("");
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get(`/companies/${companyId}/bank-statements`).then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const doParse = async (f, pw) => {
    const fd = new FormData();
    fd.append("file", f);
    if (pw) fd.append("password", pw);
    setUploading(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/bank-statements/parse`, fd);
      if (data.needs_password) { setPwFile(f); setPwError(pw ? (data.error || "Incorrect password") : ""); return; }
      setPwFile(null); setPwValue("");
      toast.success("Statement parsed — review & edit below");
      load(); setActive(data);
    } catch { toast.error("Could not parse this file"); }
    finally { setUploading(false); }
  };

  const onFile = (e) => { const f = e.target.files?.[0]; if (f) doParse(f); if (fileRef.current) fileRef.current.value = ""; };

  const createBlank = async () => {
    const { data } = await api.post(`/companies/${companyId}/bank-statements`, { bank: "IDFC FIRST Bank", label: "New statement" });
    toast.success("Blank statement created"); load(); setActive(data);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this statement?")) return;
    await api.delete(`/companies/${companyId}/bank-statements/${id}`);
    toast.success("Deleted"); load();
  };

  if (active) return <Editor companyId={companyId} initial={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="bank-statements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Bank Statements</h1>
          <p className="text-slate-500 text-sm mt-0.5">Upload a PDF/Excel statement, edit the header, period & amounts, then download the final PDF or Excel.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="bs-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bs-upload-btn"
            className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload statement
          </button>
          <button onClick={createBlank} data-testid="bs-new-btn"
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-semibold text-sm flex items-center gap-2">
            <Plus size={16} /> New blank
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-indigo-600" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-3">
          <Landmark size={30} /> No statements yet. Upload a bank statement or create a blank one.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => (
            <button key={s.id} onClick={() => setActive(s)} data-testid={`bs-card-${s.id}`}
              className="text-left bg-white rounded-2xl border border-slate-200 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:border-indigo-300 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-rose-50 grid place-items-center shrink-0"><Landmark size={20} className="text-rose-700" /></div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{s.bank || "Bank"}</p>
                  <p className="text-xs text-slate-400 truncate">{s.label} · {(s.transactions || []).length} txns</p>
                </div>
                <Trash2 size={15} className="text-slate-300 hover:text-rose-500 ml-auto" onClick={(e) => { e.stopPropagation(); del(s.id); }} data-testid={`bs-delete-${s.id}`} />
              </div>
            </button>
          ))}
        </div>
      )}

      {pwFile && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="bs-password-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-100 grid place-items-center"><Lock size={20} className="text-indigo-600" /></div>
              <h3 className="font-heading font-bold text-slate-800">Password required</h3>
            </div>
            <p className="text-sm text-slate-500">This PDF is password-protected. Enter the password to unlock and parse it.</p>
            <input type="password" autoFocus value={pwValue} onChange={(e) => setPwValue(e.target.value)} data-testid="bs-password-input"
              onKeyDown={(e) => { if (e.key === "Enter" && pwValue.trim()) doParse(pwFile, pwValue.trim()); }}
              placeholder="Document password" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20" />
            {pwError && <p className="text-sm text-rose-600">{pwError}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setPwFile(null); setPwValue(""); setPwError(""); }} className="px-4 py-2 rounded-xl border border-slate-200 font-semibold text-sm text-slate-600">Cancel</button>
              <button onClick={() => doParse(pwFile, pwValue.trim())} disabled={uploading || !pwValue.trim()} data-testid="bs-password-submit"
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Editor({ companyId, initial, onBack }) {
  const [header, setHeader] = useState(initial.header || {});
  const [rows, setRows] = useState(initial.transactions || []);
  const [bank, setBank] = useState(initial.bank || "");
  const [label, setLabel] = useState(initial.label || "");
  const [saving, setSaving] = useState(false);
  const [dl, setDl] = useState("");

  const setH = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const applyBankTemplate = (value) => {
    setBank(value);
    setHeader((h) => ({ ...h, bank: value, ...(BANK_SAMPLES[value] || {}) }));
    if (value) toast.success(`${value} template applied — branch, IFSC & MICR auto-filled`);
  };
  const setCell = (r, key, v) => setRows((rs) => rs.map((row, i) => (i === r ? { ...row, [key]: v } : row)));
  const addRow = () => setRows((rs) => [...rs, { txn_date: "", value_date: "", particulars: "", tran_type: "", cheque_details: "", debit: null, credit: null, balance: null }]);
  const delRow = (r) => setRows((rs) => rs.filter((_, i) => i !== r));

  const recalc = () => {
    setRows((rs) => {
      let bal = parseFloat(header.opening_balance);
      if (isNaN(bal)) bal = 0;
      return rs.map((row) => {
        const d = parseFloat(row.debit) || 0;
        const c = parseFloat(row.credit) || 0;
        bal = bal + c - d;
        return { ...row, balance: Number(bal.toFixed(2)) };
      });
    });
    toast.success("Balances recalculated from opening balance");
  };

  const save = async () => {
    setSaving(true);
    const clean = rows.map((r) => ({ ...r, debit: num(r.debit), credit: num(r.credit), balance: num(r.balance) }));
    try {
      await api.put(`/companies/${companyId}/bank-statements/${initial.id}`, { bank: bank || header.bank, label, header: { ...header, bank: bank || header.bank }, transactions: clean });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const download = async (fmt) => {
    setDl(fmt);
    try {
      await save();
      const res = await api.get(`/companies/${companyId}/bank-statements/${initial.id}/download?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(label || "statement").replace(/\//g, "-")}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDl(""); }
  };

  const saveToDocs = async () => {
    setDl("docs");
    try {
      await save();
      const { data } = await api.post(`/companies/${companyId}/bank-statements/${initial.id}/save-to-documents`);
      toast.success(`Saved to Documents → Bank Statements (${data.name})`);
    } catch { toast.error("Could not save to Documents"); }
    finally { setDl(""); }
  };

  const parseNum = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return isNaN(n) ? 0 : n;
  };
  const totD = rows.reduce((s, r) => s + parseNum(r.debit), 0);
  const totC = rows.reduce((s, r) => s + parseNum(r.credit), 0);
  const openingBal = parseNum(header.opening_balance);
  const closingBal = openingBal + totC - totD;
  const inr = (n) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const runningBalances = (() => {
    let b = openingBal;
    return rows.map((r) => { b = Math.round((b + parseNum(r.credit) - parseNum(r.debit)) * 100) / 100; return b; });
  })();
  const isFederal = /federal/i.test(bank || header.bank || "");
  const cols = isFederal ? FED_COLS : COLS;
  const keys = isFederal ? FED_KEYS : KEYS;
  const headerFields = isFederal ? HEADER_FIELDS.concat(FEDERAL_FIELDS) : HEADER_FIELDS;
  const rightCols = ["Debit", "Credit", "Balance", "Withdrawals", "Deposits"];

  return (
    <div className="space-y-5 animate-fade-up" data-testid="bs-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="bs-back"><ArrowLeft size={16} /> Back</button>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={recalc} data-testid="bs-recalc" className="px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold flex items-center gap-1.5"><Calculator size={15} /> Recalc balances</button>
          <button onClick={save} disabled={saving} data-testid="bs-save" className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
          <button onClick={saveToDocs} disabled={!!dl} data-testid="bs-save-to-docs" className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-60">{dl === "docs" ? <Loader2 size={15} className="animate-spin" /> : <FolderPlus size={15} />} Save to Documents</button>
          <button onClick={() => download("pdf")} disabled={!!dl} data-testid="bs-download-pdf" className="px-3.5 py-2 rounded-xl bg-rose-700 hover:bg-rose-800 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-60">{dl === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} PDF</button>
          <button onClick={() => download("xlsx")} disabled={!!dl} data-testid="bs-download-xlsx" className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-60">{dl === "xlsx" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Excel</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="bs-label" placeholder="Statement label" className="text-lg font-heading font-bold text-slate-800 outline-none border-b border-transparent focus:border-slate-300 flex-1 min-w-0" />
        </div>
        <div className="mb-4">
          <span className="text-xs font-semibold text-slate-500">Bank template (statement layout)</span>
          <select value={BANK_OPTIONS.includes(bank || header.bank) ? (bank || header.bank) : ""} data-testid="bs-bank-template"
            onChange={(e) => applyBankTemplate(e.target.value)}
            className="mt-1 block w-full sm:w-80 px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white">
            <option value="">Custom / other</option>
            {BANK_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1">Choose a bank to match the downloaded PDF branding & layout. Federal Bank uses its own Withdrawals/Deposits format with extra header fields.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {headerFields.map(([k, lbl]) => (
            <label key={k} className="block">
              <span className="text-xs font-semibold text-slate-500">{lbl}</span>
              <input value={k === "bank" ? (bank || header.bank || "") : (header[k] ?? "")} data-testid={`bs-h-${k}`}
                onChange={(e) => (k === "bank" ? (setBank(e.target.value), setH("bank", e.target.value)) : setH(k, e.target.value))}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20" />
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bs-summary">
        {[
          ["Opening Balance", openingBal, "text-slate-800"],
          ["Total Debit", totD, "text-rose-600"],
          ["Total Credit", totC, "text-emerald-600"],
          ["Closing Balance", closingBal, "text-indigo-700"],
        ].map(([lbl, val, cls]) => (
          <div key={lbl} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`bs-summary-${lbl.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-xs font-semibold text-slate-500">{lbl}</p>
            <p className={`text-lg font-heading font-bold mt-0.5 ${cls}`}>₹{inr(val)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="font-semibold text-slate-700 text-sm">Transactions ({rows.length})</p>
          <button onClick={addRow} data-testid="bs-add-row" className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50 flex items-center gap-1"><Plus size={13} /> Add row</button>
        </div>
        <div className="overflow-auto">
          <table className={`w-full text-sm ${isFederal ? "min-w-[940px]" : "min-w-[760px]"}`}>
            <thead><tr className="bg-slate-50 text-slate-500">
              <th className="w-8" />
              {cols.map((c) => <th key={c} className={`text-left px-3 py-2 font-semibold text-xs ${rightCols.includes(c) ? "text-right" : ""} ${c === "Tran Type" ? "text-center" : ""}`}>{c}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="border-t border-slate-100">
                  <td className="text-center"><button onClick={() => delRow(r)} data-testid={`bs-row-del-${r}`} className="text-slate-300 hover:text-rose-500"><X size={14} /></button></td>
                  {keys.map((key) => (
                    <td key={key} className="p-0 border-l border-slate-100">
                      {key === "balance" ? (
                        <div data-testid={`bs-cell-${r}-balance`} className="w-full px-3 py-2 text-right text-slate-500 tabular-nums bg-slate-50/60" title="Auto-computed running balance">{inr(runningBalances[r])}</div>
                      ) : (
                        <input value={row[key] ?? ""} onChange={(e) => setCell(r, key, e.target.value)} data-testid={`bs-cell-${r}-${key}`}
                          className={`w-full px-3 py-2 outline-none focus:bg-indigo-50 ${["debit", "credit"].includes(key) ? "text-right" : ""} ${key === "tran_type" ? "text-center" : ""} ${key === "particulars" ? "min-w-[220px]" : ""}`} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-slate-50 border-t border-slate-200 font-bold text-slate-700">
              <td /><td className="px-3 py-2 text-xs" colSpan={keys.indexOf("debit")}>Totals</td>
              <td className="px-3 py-2 text-right">{totD.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
              <td className="px-3 py-2 text-right">{totC.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
              <td className="px-3 py-2 text-right text-indigo-700" data-testid="bs-foot-closing">{inr(closingBal)}</td>
            </tr></tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
